import { cn } from '@rocco/utils/cn';

const HDE_OPTION_BASE_LIGHT =
    'flex w-full cursor-pointer items-center gap-[0.5278rem] self-stretch rounded-[0.7916rem] px-[0.7916rem] py-[0.9676rem] transition-colors';

const HDE_OPTION_UNSELECTED_LIGHT =
    'border border-transparent bg-[#FDFDFD] md:hover:border-[#D4D4D4]';

/** Selected option (e.g. a prefilled choice from an existing registration): visible
 *  border so the member can see which answer they previously gave on each step. */
const HDE_OPTION_SELECTED_LIGHT = 'border border-[#38383B] bg-[#FDFDFD]';

/**
 * Get option container class for HDE option list.
 */
export function getHdeOptionClassName(isSelected: boolean): string {
    return cn(
        HDE_OPTION_BASE_LIGHT,
        isSelected ? HDE_OPTION_SELECTED_LIGHT : HDE_OPTION_UNSELECTED_LIGHT,
    );
}

/** Wrapper for a titled step section (title + content) with consistent vertical padding. */
export function getHdeStepSectionClassName(): string {
    return 'flex flex-col gap-3 md:gap-[1.375rem] py-4 md:pt-15 md:py-0';
}

/** Step title style matching FormHeader h1 – use for main headings (e.g. "Are you bringing a guest?"). */
export function getHdeStepTitleClassName(): string {
    return cn(
        'block text-[1.3rem] font-medium uppercase leading-[1.1] tracking-[-0.0263rem]',
        'text-gray-900',
    );
}

export function getHdeCheckboxClass(): string {
    return cn(
        'size-5 shrink-0 rounded-[0.3125rem] border bg-transparent',
        'border-[#9e9e9e] data-[state=checked]:border-[#38383B] data-[state=checked]:bg-[#38383B] data-[state=checked]:text-white',
    );
}

/**
 * Multi-select option container styles matching Figma node 180:4323.
 * bg #FDFDFD, rounded 0.7916rem, gap 0.5278rem, padding 0.7916rem 0.9676rem.
 */
export function getHdeMultiOptionClassName(isSelected: boolean): string {
    const base =
        'flex min-h-[2.5948rem] w-full cursor-pointer items-center gap-[0.5278rem] self-stretch rounded-[0.7916rem] px-[0.7916rem] py-[0.9676rem] transition-colors';
    return (
        base +
        (isSelected
            ? ' border border-[#38383B] bg-[#FDFDFD]'
            : ' border border-transparent bg-[#FDFDFD] md:hover:border-[#D4D4D4]')
    );
}

/**
 * Multi-select checkbox styles matching Figma node 180:4323.
 * 0.6975rem square, rounded 0.25rem, border #CECECE 0.0291rem.
 *
 * The `!` important modifiers on the data-[state=checked] rules win over the
 * Rocco Checkbox's default-color variant (which otherwise wedges
 * `data-[state=checked]:bg-gray-900` into the merged class string so the prefilled
 * box stays the wrong colour after twMerge).
 */
export function getHdeMultiCheckboxClassName(): string {
    return 'size-[0.6975rem] shrink-0 rounded-[0.25rem] border-[0.0291rem] border-[#CECECE]! bg-transparent! data-[state=checked]:border-[#38383B]! data-[state=checked]:bg-[#38383B]! data-[state=checked]:text-white!';
}

/**
 * Multi-select label styles matching Figma node 180:4323.
 * 0.875rem Roman, #2A2A2A 80%, leading 1.005.
 */
export function getHdeMultiLabelClassName(): string {
    return 'text-[0.875rem] font-normal leading-[1.005] text-[#2A2A2A]/80';
}

/**
 * Input styling matching Figma HDE design (node 180:4445).
 * bg #FDFDFD, no border, rounded 0.7916rem, padding 0.7916rem 0.9676rem,
 * placeholder 0.875rem black/30%, tracking -0.0175rem.
 *
 * Uses ! to override Rocco Input default border/ring so HDE styling is consistent
 * across auth and other steps. Mobile Safari keeps a 16px font-size via
 * `customSafari.css` to avoid focus zoom.
 */
export function getHdeInputClassName(): string {
    return 'hde-mobile-safari-input min-h-[3rem] w-full rounded-[0.7916rem] border-0! border-transparent! bg-[#FDFDFD] px-[0.7916rem] py-[0.9676rem] font-normal text-[0.875rem] text-black md:text-base placeholder:text-[0.875rem] placeholder:font-normal placeholder:tracking-[-0.0175rem] placeholder:text-black/30 focus:border! focus:border-[#38383B]! focus-visible:border! focus-visible:border-[#38383B]! focus:bg-[#FDFDFD] focus:text-black focus:outline-hidden focus-visible:ring-0 focus-visible:ring-offset-0';
}

/**
 * Description/subtitle text style for option with-description variant.
 * Matches Figma node 180:4367 – 12.103px (0.7564rem) Roman, #858585, tracking -0.2421px, leading 1.1.
 */
export function getHdeOptionDescriptionClassName(): string {
    return 'text-[0.7564rem] font-normal leading-[1.1] tracking-[-0.0151rem] text-[#858585]';
}

/** Field label style (e.g. GUEST NAME, GUEST EMAIL) – matches Figma 0.7803rem uppercase #161616. */
export function getHdeFieldLabelClassName(): string {
    return cn(
        'block font-medium uppercase tracking-[-0.0156rem]',
        'text-[0.7803rem] text-[#161616]',
    );
}
